/**
 * PlatformTestInterface.js
 * Testing interface for model-specific platform positioning system
 * 
 * Provides comprehensive testing controls and visual feedback for
 * validating the automatic platform height adjustment functionality.
 */

import { getPlatformConfig, getHeightCategories, getModelsByHeight } from './ModelPlatformConfig.js';
import { characterData } from './characterData.js';

export class PlatformTestInterface {
    constructor(modelViewer) {
        this.modelViewer = modelViewer;
        this.testResults = [];
        this.currentTestModel = null;
        
        this.createTestInterface();
        this.setupTestControls();
    }

    /**
     * Create the comprehensive test interface
     */
    createTestInterface() {
        // Create test panel if it doesn't exist
        let testPanel = document.getElementById('platform-test-panel');
        if (!testPanel) {
            testPanel = document.createElement('div');
            testPanel.id = 'platform-test-panel';
            testPanel.className = 'position-fixed bg-dark text-light p-3 border border-secondary rounded';
            testPanel.style.cssText = `
                top: 20px;
                right: 20px;
                width: 350px;
                max-height: 80vh;
                overflow-y: auto;
                z-index: 1000;
                display: none;
            `;
            document.body.appendChild(testPanel);
        }

        testPanel.innerHTML = `
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">
                    <i class="fas fa-vials"></i> Platform Test Suite
                </h5>
                <button id="close-test-panel" class="btn btn-sm btn-outline-light">
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <div class="mb-3">
                <h6>Model-Specific Positioning Test</h6>
                <div class="mb-2">
                    <select id="test-model-selector" class="form-select form-select-sm">
                        <option value="">Select a model to test...</option>
                    </select>
                </div>
                <div class="d-flex gap-2 mb-2">
                    <button id="load-test-model" class="btn btn-primary btn-sm">
                        <i class="fas fa-play"></i> Load Model
                    </button>
                    <button id="test-all-heights" class="btn btn-info btn-sm">
                        <i class="fas fa-flask"></i> Test All Heights
                    </button>
                </div>
            </div>

            <div class="mb-3">
                <h6>Current Model Info</h6>
                <div id="current-test-info" class="small text-muted">
                    No model loaded for testing
                </div>
            </div>

            <div class="mb-3">
                <h6>Height Categories</h6>
                <div id="height-category-stats" class="small">
                    Loading category statistics...
                </div>
            </div>

            <div class="mb-3">
                <h6>Test Results</h6>
                <div id="test-results" style="max-height: 200px; overflow-y: auto;">
                    <div class="text-muted small">No tests run yet</div>
                </div>
                <button id="clear-test-results" class="btn btn-outline-warning btn-sm mt-2">
                    <i class="fas fa-trash"></i> Clear Results
                </button>
            </div>

            <div>
                <h6>Quick Actions</h6>
                <div class="d-flex flex-wrap gap-1">
                    <button id="test-lincoln" class="btn btn-success btn-sm">Lincoln (Tall)</button>
                    <button id="test-marie" class="btn btn-warning btn-sm">Marie (Short)</button>
                    <button id="test-feynman" class="btn btn-secondary btn-sm">Feynman (Avg)</button>
                </div>
            </div>
        `;

        this.populateModelSelector();
        this.updateHeightCategoryStats();
    }

    /**
     * Populate the model selector with available models
     */
    populateModelSelector() {
        const selector = document.getElementById('test-model-selector');
        if (!selector) return;

        // Clear existing options except the first one
        selector.innerHTML = '<option value="">Select a model to test...</option>';

        // Group models by height category
        const categories = getHeightCategories();
        categories.forEach(category => {
            const models = getModelsByHeight(category);
            if (models.length > 0) {
                const optgroup = document.createElement('optgroup');
                optgroup.label = category.replace('_', ' ').toUpperCase();
                
                models.forEach(modelId => {
                    const config = getPlatformConfig(modelId);
                    const option = document.createElement('option');
                    option.value = modelId;
                    option.textContent = `${config.name} (Height: ${config.platformHeight})`;
                    optgroup.appendChild(option);
                });
                
                selector.appendChild(optgroup);
            }
        });
    }

    /**
     * Update height category statistics display
     */
    updateHeightCategoryStats() {
        const statsElement = document.getElementById('height-category-stats');
        if (!statsElement) return;

        const categories = getHeightCategories();
        const stats = categories.map(category => {
            const models = getModelsByHeight(category);
            const avgHeight = models.length > 0 ? 
                models.reduce((sum, modelId) => sum + getPlatformConfig(modelId).platformHeight, 0) / models.length : 0;
            
            return `
                <div class="d-flex justify-content-between">
                    <span>${category.replace('_', ' ')}</span>
                    <span>${models.length} models (avg: ${avgHeight.toFixed(1)})</span>
                </div>
            `;
        }).join('');

        statsElement.innerHTML = stats;
    }

    /**
     * Set up event listeners for test controls
     */
    setupTestControls() {
        // Close panel
        const closeBtn = document.getElementById('close-test-panel');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                this.hideTestPanel();
            });
        }

        // Load selected model
        const loadBtn = document.getElementById('load-test-model');
        if (loadBtn) {
            loadBtn.addEventListener('click', () => {
                this.loadSelectedTestModel();
            });
        }

        // Test all heights
        const testAllBtn = document.getElementById('test-all-heights');
        if (testAllBtn) {
            testAllBtn.addEventListener('click', () => {
                this.runComprehensiveHeightTest();
            });
        }

        // Clear results
        const clearBtn = document.getElementById('clear-test-results');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                this.clearTestResults();
            });
        }

        // Quick action buttons
        document.getElementById('test-lincoln')?.addEventListener('click', () => {
            this.quickLoadModel('0fd73e9d-bbf7-4d2e-989a-cda8fb062c73'); // Abraham Lincoln
        });

        document.getElementById('test-marie')?.addEventListener('click', () => {
            this.quickLoadModel('6da2e341-3d4e-4a06-bf8c-5cb8f499b93e'); // Marie Curie
        });

        document.getElementById('test-feynman')?.addEventListener('click', () => {
            this.quickLoadModel('bc5d31a8-047b-4f72-8f16-fea7a365ca69'); // Richard Feynman
        });

        // Add test panel toggle to main menu
        this.addTestPanelToMenu();
    }

    /**
     * Add test panel toggle to the main menu
     */
    addTestPanelToMenu() {
        const menuItems = document.querySelector('#menuButton + .dropdown-menu');
        if (menuItems) {
            const testMenuItem = document.createElement('a');
            testMenuItem.className = 'dropdown-item';
            testMenuItem.href = '#';
            testMenuItem.innerHTML = '<i class="fas fa-vials"></i> Platform Test Suite';
            testMenuItem.addEventListener('click', (e) => {
                e.preventDefault();
                this.showTestPanel();
            });
            menuItems.appendChild(testMenuItem);
        }
    }

    /**
     * Show the test panel
     */
    showTestPanel() {
        const panel = document.getElementById('platform-test-panel');
        if (panel) {
            panel.style.display = 'block';
            this.updateCurrentModelInfo();
        }
    }

    /**
     * Hide the test panel
     */
    hideTestPanel() {
        const panel = document.getElementById('platform-test-panel');
        if (panel) {
            panel.style.display = 'none';
        }
    }

    /**
     * Load the selected test model
     */
    async loadSelectedTestModel() {
        const selector = document.getElementById('test-model-selector');
        const modelId = selector?.value;
        
        if (!modelId) {
            this.addTestResult('No model selected', 'warning');
            return;
        }

        await this.quickLoadModel(modelId);
    }

    /**
     * Quick load a specific model for testing
     */
    async quickLoadModel(modelId) {
        if (!this.modelViewer || !this.modelViewer.modelManager) {
            this.addTestResult('Model viewer not available', 'error');
            return;
        }

        const config = getPlatformConfig(modelId);
        this.addTestResult(`Loading ${config.name} for platform height testing...`, 'info');
        
        try {
            // Record test start
            const testStart = performance.now();
            
            // Load the model
            await this.modelViewer.modelManager.setCurrentModel(modelId, false);
            
            // Wait a moment for platform to set up
            setTimeout(() => {
                const testEnd = performance.now();
                const duration = (testEnd - testStart).toFixed(0);
                
                this.validateModelPlatformSetup(modelId, duration);
                this.updateCurrentModelInfo();
            }, 1000);
            
        } catch (error) {
            this.addTestResult(`Failed to load ${config.name}: ${error.message}`, 'error');
        }
    }

    /**
     * Validate that the model platform setup is working correctly
     */
    validateModelPlatformSetup(modelId, loadDuration) {
        const config = getPlatformConfig(modelId);
        let validationResults = [];

        // Check if platform exists
        if (this.modelViewer.platform) {
            const platformY = this.modelViewer.platform.position.y;
            const expectedY = config.platformHeight;
            
            if (Math.abs(platformY - expectedY) < 1) {
                validationResults.push(`✓ Platform height correct: ${platformY}`);
            } else {
                validationResults.push(`✗ Platform height mismatch: ${platformY} (expected: ${expectedY})`);
            }
        } else {
            validationResults.push('✗ Platform not found');
        }

        // Check if model exists and positioned correctly
        if (this.modelViewer.model) {
            const modelY = this.modelViewer.model.position.y;
            const expectedY = config.modelYOffset;
            
            if (Math.abs(modelY - expectedY) < 1) {
                validationResults.push(`✓ Model Y offset correct: ${modelY}`);
            } else {
                validationResults.push(`✗ Model Y offset mismatch: ${modelY} (expected: ${expectedY})`);
            }
        } else {
            validationResults.push('✗ Model not found');
        }

        // Check camera positioning
        if (this.modelViewer.camera) {
            const cameraY = this.modelViewer.camera.position.y;
            validationResults.push(`ℹ Camera Y position: ${cameraY.toFixed(1)}`);
        }

        const resultType = validationResults.some(r => r.includes('✗')) ? 'error' : 'success';
        const resultText = `${config.name} (${loadDuration}ms):\n${validationResults.join('\n')}`;
        
        this.addTestResult(resultText, resultType);
    }

    /**
     * Run comprehensive height testing across all categories
     */
    async runComprehensiveHeightTest() {
        this.addTestResult('Starting comprehensive height test...', 'info');
        
        const categories = getHeightCategories();
        let totalTests = 0;
        let passedTests = 0;

        for (const category of categories) {
            const models = getModelsByHeight(category);
            if (models.length > 0) {
                // Test one model from each category
                const testModelId = models[0];
                const config = getPlatformConfig(testModelId);
                
                this.addTestResult(`Testing ${category}: ${config.name}`, 'info');

                // Anchor-based validation: a derived pose is "valid" when
                // its computed values are finite numbers and the eye height
                // is in a sane positive range. The legacy 10–50 platform
                // height check no longer applies — platformY is anchor-
                // derived and is often outside that range.
                const isValid =
                    Number.isFinite(config.platformHeight) &&
                    Number.isFinite(config.modelYOffset) &&
                    Number.isFinite(config.platformScale) &&
                    config.platformScale > 0 &&
                    Number.isFinite(config.eyeHeight) &&
                    config.eyeHeight > 0 &&
                    config.eyeHeight < 1000;

                if (isValid) {
                    passedTests++;
                    this.addTestResult(`✓ ${config.name} configuration valid`, 'success');
                } else {
                    this.addTestResult(`✗ ${config.name} configuration invalid`, 'error');
                }
                
                totalTests++;
            }
        }

        this.addTestResult(`Test Summary: ${passedTests}/${totalTests} configurations valid`, 
                          passedTests === totalTests ? 'success' : 'warning');
    }

    /**
     * Update current model information display
     */
    updateCurrentModelInfo() {
        const infoElement = document.getElementById('current-test-info');
        if (!infoElement) return;

        if (this.modelViewer && this.modelViewer.currentModelKey) {
            const config = getPlatformConfig(this.modelViewer.currentModelKey);
            const platformHeight = this.modelViewer.platform ? 
                this.modelViewer.platform.position.y.toFixed(1) : 'N/A';
            const modelHeight = this.modelViewer.model ? 
                this.modelViewer.model.position.y.toFixed(1) : 'N/A';

            infoElement.innerHTML = `
                <strong>${config.name}</strong><br>
                Category: ${config.heightCategory.replace('_', ' ')}<br>
                Platform Height: ${platformHeight} (config: ${config.platformHeight})<br>
                Model Y Offset: ${modelHeight} (config: ${config.modelYOffset})
            `;
        } else {
            infoElement.textContent = 'No model loaded for testing';
        }
    }

    /**
     * Add a test result to the results display
     */
    addTestResult(message, type = 'info') {
        const resultsElement = document.getElementById('test-results');
        if (!resultsElement) return;

        const timestamp = new Date().toLocaleTimeString();
        const colorClass = {
            'success': 'text-success',
            'error': 'text-danger',
            'warning': 'text-warning',
            'info': 'text-info'
        }[type] || 'text-light';

        const resultDiv = document.createElement('div');
        resultDiv.className = `small mb-1 ${colorClass}`;
        resultDiv.innerHTML = `
            <span class="text-muted">[${timestamp}]</span> 
            ${message.replace(/\n/g, '<br>')}
        `;

        // If results container is empty, clear the "no tests" message
        if (resultsElement.children.length === 1 && 
            resultsElement.children[0].textContent.includes('No tests')) {
            resultsElement.innerHTML = '';
        }

        resultsElement.appendChild(resultDiv);
        resultsElement.scrollTop = resultsElement.scrollHeight;

        // Store result for potential export
        this.testResults.push({
            timestamp: new Date(),
            message,
            type
        });
    }

    /**
     * Clear all test results
     */
    clearTestResults() {
        const resultsElement = document.getElementById('test-results');
        if (resultsElement) {
            resultsElement.innerHTML = '<div class="text-muted small">No tests run yet</div>';
        }
        this.testResults = [];
    }
}